import React from 'react';
import { Collapse } from 'antd';
import ConfigToggleOption from '../../baseComponents/ConfigToggleOption';
import type { FormInstance } from 'antd/es/form';
import { BuilderSettingType, useBuilderSettingContext } from '../../../sampleApp/src/context/BuilderSettingsContext';

/**
 * Props for the ChatFeatures component.
 * @typedef {Object} ChatFeaturesProps
 * @property {FormInstance} form - Ant Design form instance for managing form state.
 */
interface ChatFeaturesProps {
  form: FormInstance;
}

const { Panel } = Collapse;
const inFoTitle = 'To enable this feature, navigate to Chat > Features in your dashboard and toggle it on.';
const mentionsTitle =
  'Turning this off will disable mentions for new messages, but existing mentions will remain unaffected.';
const reactionsTitle = 'Turning this off will disable reactions, but existing reactions will remain unaffected.';

/**
 * ChatFeatures component provides a UI to enable or disable chat features.
 * @param {ChatFeaturesProps} props - Component props.
 * @returns {JSX.Element} The rendered ChatFeatures component.
 */
const ChatFeatures = ({ form }: ChatFeaturesProps) => {
  const { chatFeatures, setChatFeatures } = useBuilderSettingContext();

  /**
   * Handles the toggling of chat features.
   * @param {keyof BuilderSettingType['chatFeatures']} category - The category of the chat feature.
   * @param {string} type - The specific feature type.
   * @param {boolean} value - The new state of the feature.
   */
  const handleFeatureToggle = (category: keyof BuilderSettingType['chatFeatures'], type: string, value: boolean) => {
    const currentChatFeatures = form.getFieldValue('chatFeatures') || {};
    const currentCategory = currentChatFeatures[category] || {};

    const updatedChatFeatures = {
      ...currentChatFeatures,
      [category]: {
        ...currentCategory,
        [type]: value,
      },
    };

    form.setFieldsValue({ chatFeatures: updatedChatFeatures });
    setChatFeatures(updatedChatFeatures);
  };

  return (
    <div id="chatFeatureWrapper" className="chatFeatureWrapper">
      <div className="featureHeader chatFeaturesHeader">Chat Features</div>
      <div className="chatFeaturesOptions">
        <Collapse
          className="commonCollapse callCollapse"
          defaultActiveKey={['core-messaging-experience']}
          bordered={false}
          expandIconPosition="end"
          expandIcon={({ isActive }) =>
            isActive ? <div className="panelHeaderCollapseIcon" /> : <div className="panelHeaderExpandIcon" />
          }
          accordion
        >
          <Panel
            header={<div className="panelHeader chatPanelHeader">Core messaging experience</div>}
            key="core-messaging-experience"
          >
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'typingIndicator']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.typingIndicator}
              label="Typing indicator"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'typingIndicator', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'threadConversationAndReplies']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.threadConversationAndReplies}
              label="Thread conversation & replies"
              onChange={(value) =>
                handleFeatureToggle('coreMessagingExperience', 'threadConversationAndReplies', value)
              }
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'photosSharing']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.photosSharing}
              label="Photos sharing"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'photosSharing', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'videoSharing']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.videoSharing}
              label="Video sharing"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'videoSharing', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'audioSharing']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.audioSharing}
              label="Audio sharing"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'audioSharing', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'fileSharing']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.fileSharing}
              label="File sharing"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'fileSharing', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'editMessage']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.editMessage}
              label="Edit  messages"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'editMessage', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'deleteMessage']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.deleteMessage}
              label=" Delete messages"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'deleteMessage', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'messageDeliveryAndReadReceipts']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.messageDeliveryAndReadReceipts}
              label="Message delivery and read receipts"
              onChange={(value) =>
                handleFeatureToggle('coreMessagingExperience', 'messageDeliveryAndReadReceipts', value)
              }
            />

            <ConfigToggleOption
              name={['chatFeatures', 'coreMessagingExperience', 'userAndFriendsPresence']}
              isFeatureEnabled={chatFeatures?.coreMessagingExperience?.userAndFriendsPresence}
              label="User & friends presence (online & offline status)"
              onChange={(value) => handleFeatureToggle('coreMessagingExperience', 'userAndFriendsPresence', value)}
            />
          </Panel>
          <Panel
            header={<div className="panelHeader chatPanelHeader">Deeper user engagement</div>}
            key="deeper-user-engagement"
          >
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'mentions']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.mentions}
              label="Mentions"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'mentions', value)}
              infoTitle={mentionsTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'reactions']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.reactions}
              label="Reactions"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'reactions', value)}
              infoTitle={reactionsTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'messageTranslation']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.messageTranslation}
              label="Message translation"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'messageTranslation', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'polls']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.polls}
              label="Polls"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'polls', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'collaborativeWhiteboard']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.collaborativeWhiteboard}
              label="Collaborative whiteboard"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'collaborativeWhiteboard', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'collaborativeDocument']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.collaborativeDocument}
              label="Collaborative document"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'collaborativeDocument', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'voiceNotes']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.voiceNotes}
              label="Voice notes"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'voiceNotes', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'emojis']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.emojis}
              label="Emojis"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'emojis', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'stickers']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.stickers}
              label="Stickers"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'stickers', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'userInfo']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.userInfo}
              label="User Info"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'userInfo', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'deeperUserEngagement', 'groupInfo']}
              isFeatureEnabled={chatFeatures?.deeperUserEngagement?.groupInfo}
              label="Group Info"
              onChange={(value) => handleFeatureToggle('deeperUserEngagement', 'groupInfo', value)}
            />
          </Panel>
          <Panel header={<div className="panelHeader chatPanelHeader">AI user copilot</div>} key="ai-user-copilot">
            <ConfigToggleOption
              name={['chatFeatures', 'aiUserCopilot', 'conversationStarter']}
              isFeatureEnabled={chatFeatures?.aiUserCopilot?.conversationStarter}
              label="Conversation starter"
              onChange={(value) => handleFeatureToggle('aiUserCopilot', 'conversationStarter', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'aiUserCopilot', 'conversationSummary']}
              isFeatureEnabled={chatFeatures?.aiUserCopilot?.conversationSummary}
              label="Conversation summary"
              onChange={(value) => handleFeatureToggle('aiUserCopilot', 'conversationSummary', value)}
              infoTitle={inFoTitle}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'aiUserCopilot', 'smartReply']}
              isFeatureEnabled={chatFeatures?.aiUserCopilot?.smartReply}
              label="Smart reply"
              onChange={(value) => handleFeatureToggle('aiUserCopilot', 'smartReply', value)}
              infoTitle={inFoTitle}
            />
          </Panel>
          <Panel header={<div className="panelHeader chatPanelHeader">Group management</div>} key="group-management">
            <ConfigToggleOption
              name={['chatFeatures', 'groupManagement', 'createGroup']}
              isFeatureEnabled={chatFeatures?.groupManagement?.createGroup}
              label="Create group"
              onChange={(value) => handleFeatureToggle('groupManagement', 'createGroup', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'groupManagement', 'addMembersToGroups']}
              isFeatureEnabled={chatFeatures?.groupManagement?.addMembersToGroups}
              label="Add members to groups (group owners)"
              onChange={(value) => handleFeatureToggle('groupManagement', 'addMembersToGroups', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'groupManagement', 'joinLeaveGroup']}
              isFeatureEnabled={chatFeatures?.groupManagement?.joinLeaveGroup}
              label="Leave group"
              onChange={(value) => handleFeatureToggle('groupManagement', 'joinLeaveGroup', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'groupManagement', 'deleteGroup']}
              isFeatureEnabled={chatFeatures?.groupManagement?.deleteGroup}
              label="Delete and exit group"
              onChange={(value) => handleFeatureToggle('groupManagement', 'deleteGroup', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'groupManagement', 'viewGroupMembers']}
              isFeatureEnabled={chatFeatures?.groupManagement?.viewGroupMembers}
              label="Group members"
              onChange={(value) => handleFeatureToggle('groupManagement', 'viewGroupMembers', value)}
            />
          </Panel>
          <Panel
            header={<div className="panelHeader chatPanelHeader">Moderator controls</div>}
            key="moderator-controls"
          >
            <ConfigToggleOption
              name={['chatFeatures', 'moderatorControls', 'kickUsers']}
              isFeatureEnabled={chatFeatures?.moderatorControls?.kickUsers}
              label="Kick users"
              onChange={(value) => handleFeatureToggle('moderatorControls', 'kickUsers', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'moderatorControls', 'banUsers']}
              isFeatureEnabled={chatFeatures?.moderatorControls?.banUsers}
              label="Ban users"
              onChange={(value) => handleFeatureToggle('moderatorControls', 'banUsers', value)}
            />
            <ConfigToggleOption
              name={['chatFeatures', 'moderatorControls', 'promoteDemoteMembers']}
              isFeatureEnabled={chatFeatures?.moderatorControls?.promoteDemoteMembers}
              label="Promote/demote members"
              onChange={(value) => handleFeatureToggle('moderatorControls', 'promoteDemoteMembers', value)}
            />
          </Panel>
          <Panel
            header={<div className="panelHeader chatPanelHeader">Private Messaging within Groups</div>}
            key="private-messaging-within-groups"
          >
            <ConfigToggleOption
              name={['chatFeatures', 'privateMessagingWithinGroups', 'sendPrivateMessageToGroupMembers']}
              isFeatureEnabled={chatFeatures?.privateMessagingWithinGroups?.sendPrivateMessageToGroupMembers}
              label="Send private message to group members"
              onChange={(value) =>
                handleFeatureToggle('privateMessagingWithinGroups', 'sendPrivateMessageToGroupMembers', value)
              }
            />
          </Panel>
        </Collapse>
      </div>
    </div>
  );
};

export default ChatFeatures;
